import { type ComponentType, type PropsWithChildren } from "react"
import { ErrorBoundary, type FallbackProps } from "react-error-boundary"
import { Grid, Typography, Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    contentWrapper: {
        width: "100%",
        height: "100%",
        padding: "1.5rem",
    },
    errorMessage: {
        textWrap: "wrap",
        fontWeight: "normal",
        color: theme.palette.text.secondary,
    },
}))

export function InlineFallbackContents(
    props:
        | FallbackProps
        | {
              icon?: JSX.Element
              errorMessage?: string
              externalClasses?: string
          },
) {
    const { classes, cx } = useStyles()

    const icon = "icon" in props ? props.icon : undefined
    const errorMessage = "errorMessage" in props ? props.errorMessage : undefined
    const externalClasses = "externalClasses" in props ? props.externalClasses : undefined

    return (
        <Grid
            container
            direction="column"
            wrap="nowrap"
            justifyContent="space-between"
            className={cx(classes.contentWrapper, externalClasses)}
        >
            <Box>{icon}</Box>
            {errorMessage ? (
                <Typography variant="subtitle1" className={classes.errorMessage}>
                    {errorMessage}
                </Typography>
            ) : (
                <LocalizedStrict id="inline-error-boundary-message">
                    <Typography variant="subtitle1" className={classes.errorMessage}>
                        Could not render contents
                    </Typography>
                </LocalizedStrict>
            )}
        </Grid>
    )
}

type Props = Readonly<
    PropsWithChildren<{
        Fallback?: ComponentType<FallbackProps>
    }>
>

export function InlineErrorBoundary({ Fallback, children }: Props) {
    return <ErrorBoundary FallbackComponent={Fallback ? Fallback : InlineFallbackContents}>{children}</ErrorBoundary>
}
